import { formatNumber } from "@/lib/format";

type Props = {
  readonly value: number;
  readonly min: number;
  readonly max: number;
  readonly step: number;
  readonly onChange: (n: number) => void;
};

const PRESETS = [1.5, 2, 3, 4] as const;

const clamp = (n: number, lo: number, hi: number) =>
  Math.min(Math.max(n, lo), hi);

export const LeverageSlider = ({ value, min, max, step, onChange }: Props) => {
  const pct = ((value - min) / (max - min)) * 100;

  return (
    <section>
      <div className="flex items-baseline justify-between mb-4">
        <h3>Leverage</h3>
        <div className="flex items-baseline gap-2">
          <span
            className="font-serif tabular"
            style={{
              fontSize: 36,
              fontWeight: 300,
              letterSpacing: "-0.02em",
              color: "var(--ink-1)",
            }}
          >
            {formatNumber(value, 2)}
          </span>
          <span
            className="font-sans text-[12px] tracking-[0.16em] uppercase"
            style={{ color: "var(--ink-3)" }}
          >
            ×
          </span>
        </div>
      </div>

      <div className="relative h-[18px] flex items-center">
        <div
          className="absolute inset-x-0 h-[2px] rounded-full"
          style={{ background: "var(--line-3)" }}
        />
        <div
          className="pointer-events-none absolute left-0 h-[2px] rounded-full"
          style={{ width: `${pct}%`, background: "var(--ink-1)" }}
        />
        <input
          type="range"
          aria-label="Leverage"
          className="slider relative z-10"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(Number.parseFloat(e.target.value))}
        />
      </div>

      <div className="flex justify-between mt-3">
        <span
          className="font-mono text-[10px] tracking-[0.16em]"
          style={{ color: "var(--ink-4)" }}
        >
          {formatNumber(min, 1)}×
        </span>
        <span
          className="font-mono text-[10px] tracking-[0.16em]"
          style={{ color: "var(--ink-4)" }}
        >
          {formatNumber(max, 1)}×
        </span>
      </div>

      <div className="flex gap-2 mt-5">
        {PRESETS.map((p) => {
          const active = Math.abs(value - p) < 0.01;
          return (
            <button
              key={p}
              type="button"
              onClick={() => onChange(clamp(p, min, max))}
              className="px-3 h-7 rounded-[2px] text-[11px] tracking-[0.12em] uppercase transition-all border"
              style={{
                background: active ? "var(--surface-2)" : "transparent",
                color: active ? "var(--ink-1)" : "var(--ink-2)",
                borderColor: active ? "var(--ink-1)" : "var(--line-2)",
                fontWeight: 500,
              }}
            >
              {formatNumber(p, p % 1 === 0 ? 0 : 1)}×
            </button>
          );
        })}
      </div>
    </section>
  );
};
